/**
 * Transactions — the desk money-history surface and the home for Void +
 * Refund (Authorize.net virtual-terminal parity).
 *
 * Lists money-moved Payment rows (GET /api/v2/admin/payments/transactions),
 * each annotated server-side with a settlement heuristic and void/refund
 * eligibility. The settlement state drives the PRIMARY action per Option A
 * (Johnny, 2026-06-29):
 *
 *   - unsettled (created after the tenant's most-recent batch cutoff)
 *       → primary Void (clean same-day cancel) + "Refund instead"
 *   - settled (created before it) → Refund (full or partial)
 *
 * The platform cannot KNOW settlement (no Authorize.net settlement
 * webhooks yet), so this is a per-tenant clock guess; the GATEWAY is the
 * arbiter. If a Void is attempted on an already-settled charge the gateway
 * rejects it and we steer the user to Refund. Money is never at risk.
 *
 * This surface also IS the owner/accountant reporting view — they no
 * longer need the Authorize.net portal for transaction history.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { adminPaymentsApi, type AdminTransaction } from '../../lib/api.js';
import { PermissionGate } from '../PermissionGate.js';

type TransactionsProps = {
  readonly apiUrl: string;
  /** Parent bumps this to force a refetch after an external money event. */
  readonly refreshKey?: number;
  /** Called after a successful void/refund so parent surfaces can refresh. */
  readonly onChanged?: () => void;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | {
      kind: 'ready';
      transactions: AdminTransaction[];
      /** True when the tenant runs the simulation gateway (demo surface). */
      simulationEnv: boolean;
    };

type RefundDialog = {
  paymentId: string;
  customerName: string;
  remainingCents: number;
  /** dollars text in the amount field; '' = full remaining */
  amountText: string;
  reason: string;
};

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * #491 — a voided charge and a refunded charge both land on the ledger's
 * single 'refunded' status, but they mean different things to an owner: a
 * void reversed an unsettled auth (no money ever moved), a refund returned
 * settled money. Prefer the server's derived `reversal`; fall back to
 * re-deriving from `refundedCents` when the field is absent (deploy skew —
 * a void never increments refunded_cents, every refund path does).
 */
function reversalLabel(t: AdminTransaction): 'Voided' | 'Refunded' {
  const kind = t.reversal ?? (t.refundedCents > 0 ? 'refunded' : 'voided');
  return kind === 'voided' ? 'Voided' : 'Refunded';
}

/**
 * #812 — human label for the stored tender. Unknown values (a future
 * server ahead of this bundle) render as-is rather than hiding.
 */
const TENDER_LABELS: Record<string, string> = {
  cash: 'Cash',
  check: 'Check',
  external_terminal: 'Card terminal',
  card_gateway: 'Card',
  package: 'Package',
  redemption: 'Points',
  other: 'Other tender',
};

function tenderLabel(method: string): string {
  return TENDER_LABELS[method] ?? method;
}

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function Transactions({
  apiUrl,
  refreshKey = 0,
  onChanged,
}: TransactionsProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmVoidId, setConfirmVoidId] = useState<string | null>(null);
  const [refund, setRefund] = useState<RefundDialog | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOk, setActionOk] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const res = await adminPaymentsApi.listTransactions(apiUrl, { limit: 100 });
    if (res.error || !res.data) {
      setState({
        kind: 'error',
        message: res.error ?? 'Could not load transactions',
      });
      return;
    }
    setState({
      kind: 'ready',
      transactions: res.data.transactions ?? [],
      simulationEnv: res.data.gatewayMode === 'simulation',
    });
  }, [apiUrl]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const afterMutation = useCallback(async () => {
    await load();
    onChanged?.();
  }, [load, onChanged]);

  const doVoid = useCallback(
    async (paymentId: string) => {
      setBusyId(paymentId);
      setActionError(null);
      setActionOk(null);
      const res = await adminPaymentsApi.voidPayment(apiUrl, paymentId);
      setBusyId(null);
      setConfirmVoidId(null);
      if (res.error || !res.data?.void) {
        setActionError(
          `${res.error ?? 'Void failed'}. If this charge already settled tonight’s batch, use Refund instead.`,
        );
        return;
      }
      // Honest without overclaiming: a simulation void is recorded in the
      // ledger (no card network involved); a live void was accepted by the
      // gateway but the batch is the final arbiter.
      setActionOk(
        res.data.void.simulation
          ? 'Void recorded — the charge has been reversed.'
          : 'Void accepted — the customer will not be charged.',
      );
      await afterMutation();
    },
    [apiUrl, afterMutation],
  );

  const doRefund = useCallback(async () => {
    if (!refund) return;
    const trimmed = refund.amountText.trim();
    let amountCents: number | undefined;
    if (trimmed !== '') {
      const dollars = Number(trimmed);
      if (!Number.isFinite(dollars) || dollars <= 0) {
        setActionError('Enter a refund amount greater than $0.');
        return;
      }
      amountCents = Math.round(dollars * 100);
      if (amountCents > refund.remainingCents) {
        setActionError(
          `Refund cannot exceed the ${formatMoney(refund.remainingCents)} still available on this charge.`,
        );
        return;
      }
    }
    setBusyId(refund.paymentId);
    setActionError(null);
    setActionOk(null);
    const res = await adminPaymentsApi.refundPayment(apiUrl, {
      paymentId: refund.paymentId,
      amountCents,
      reason: refund.reason.trim() || undefined,
    });
    setBusyId(null);
    if (res.error || !res.data?.refund) {
      setActionError(res.error ?? 'Refund failed');
      return;
    }
    const r = res.data.refund;
    // Honest without overclaiming: "recorded" for the simulation ledger;
    // live refunds are submitted to the card network, which posts them on
    // its own schedule — never claim the money already landed.
    setActionOk(
      r.simulation
        ? `Refund of ${formatMoney(r.amountCents)} recorded.`
        : `Refund of ${formatMoney(r.amountCents)} submitted — it will appear on the customer's statement within a few business days.`,
    );
    setRefund(null);
    await afterMutation();
  }, [apiUrl, refund, afterMutation]);

  const openRefund = useCallback((t: AdminTransaction) => {
    setActionError(null);
    setActionOk(null);
    setConfirmVoidId(null);
    setRefund({
      paymentId: t.paymentId,
      customerName: t.customerName || `Customer ${t.customerId.slice(0, 8)}`,
      remainingCents: t.totalCents - t.refundedCents,
      amountText: '',
      reason: '',
    });
  }, []);

  const rows = state.kind === 'ready' ? state.transactions : [];
  const simulationEnv = state.kind === 'ready' && state.simulationEnv;
  const empty = useMemo(
    () => state.kind === 'ready' && rows.length === 0,
    [state.kind, rows.length],
  );

  return (
    <article
      className="ferro-card"
      aria-label="Transactions"
      data-testid="transactions"
    >
      <header className="ferro-card__header">
        <h2 className="ferro-section__subtitle">Transactions</h2>
        {/* One honest environment-level disclosure (DESIGN-PHILOSOPHY
            Principle 5) — the badge, not per-row dev copy, says this
            tenant runs the simulation gateway. */}
        {simulationEnv ? (
          <span
            className="ferro-badge ferro-badge--neutral"
            data-testid="transactions-simulation-badge"
          >
            Simulation
          </span>
        ) : null}
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={() => void load()}
          data-testid="transactions-refresh"
        >
          Refresh
        </button>
      </header>

      <p className="ferro-muted">
        Every card payment, with refunds and same-day voids. Unsettled
        charges (before tonight’s batch) can be voided; settled charges are
        refunded.
      </p>

      {actionError ? (
        <div
          className="ferro-dashboard__error"
          role="alert"
          data-testid="transactions-action-error"
        >
          <p>{actionError}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => setActionError(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {actionOk ? (
        <div
          className="ferro-banner ferro-banner--success"
          role="status"
          data-testid="transactions-action-ok"
        >
          <p>{actionOk}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => setActionOk(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {state.kind === 'loading' ? (
        <p className="ferro-muted" data-testid="transactions-loading">
          Loading transactions…
        </p>
      ) : null}

      {state.kind === 'error' ? (
        <div className="ferro-dashboard__error" role="alert">
          <p>{state.message}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      ) : null}

      {empty ? (
        <p className="ferro-muted" data-testid="transactions-empty">
          No transactions yet.
        </p>
      ) : null}

      {rows.length > 0 ? (
        <ul className="ferro-table" aria-label="Transaction list">
          {rows.map((t) => {
            const refundedNote =
              t.refundedCents > 0
                ? ` · ${formatMoney(t.refundedCents)} refunded`
                : '';
            const isRefundDialogOpen = refund?.paymentId === t.paymentId;
            const isConfirmingVoid = confirmVoidId === t.paymentId;
            return (
              <li
                key={t.paymentId}
                className="ferro-table__row"
                data-testid={`transaction-${t.paymentId}`}
              >
                <div className="ferro-table__body">
                  <div className="ferro-table__primary">
                    {t.customerName ||
                      `Customer ${t.customerId.slice(0, 8)}`}
                    {' · '}
                    {formatMoney(t.totalCents)}
                    {t.status === 'refunded' ? (
                      <span
                        className="ferro-badge ferro-badge--neutral"
                        data-testid={`transaction-reversal-${t.paymentId}`}
                      >
                        {reversalLabel(t)}
                      </span>
                    ) : t.settlement === 'unsettled' ? (
                      <span
                        className="ferro-badge ferro-badge--warning"
                        data-testid={`transaction-badge-${t.paymentId}`}
                      >
                        Unsettled
                      </span>
                    ) : (
                      <span
                        className="ferro-badge ferro-badge--success"
                        data-testid={`transaction-badge-${t.paymentId}`}
                      >
                        Settled
                      </span>
                    )}
                  </div>
                  <div className="ferro-table__meta">
                    {formatDateTime(t.createdAt)}
                    {refundedNote}
                    {t.tipCents > 0
                      ? ` · ${formatMoney(t.tipCents)} tip`
                      : ''}
                    {/* #812 — stored tender wins; for rows without one
                        (pre-#812 / customer checkout) keep the derived
                        annotation. On the simulation gateway, sim card
                        charges read as the card transactions they stand in
                        for — the header badge, not every row, carries the
                        disclosure. On a LIVE/test tenant nothing changes:
                        legacy sim_ rows keep their honest non-card
                        annotation. */}
                    {t.paymentMethod
                      ? ` · ${tenderLabel(t.paymentMethod)}${t.checkNumber ? ` #${t.checkNumber}` : ''}`
                      : !t.gatewayBacked && !(simulationEnv && t.simulated)
                        ? ' · non-card settle'
                        : ''}
                  </div>
                </div>

                <div className="ferro-table__actions">
                  <PermissionGate permission="payments:refund:all">
                    {isConfirmingVoid ? (
                      <span
                        className="ferro-inline-confirm"
                        data-testid={`void-confirm-${t.paymentId}`}
                      >
                        <span className="ferro-muted">Void this charge?</span>
                        <button
                          type="button"
                          className="ferro-btn ferro-btn--danger"
                          disabled={busyId === t.paymentId}
                          onClick={() => void doVoid(t.paymentId)}
                          data-testid={`void-confirm-yes-${t.paymentId}`}
                        >
                          {busyId === t.paymentId ? 'Voiding…' : 'Confirm void'}
                        </button>
                        <button
                          type="button"
                          className="ferro-btn ferro-btn--ghost"
                          onClick={() => setConfirmVoidId(null)}
                        >
                          Cancel
                        </button>
                      </span>
                    ) : isRefundDialogOpen ? null : (
                      <>
                        {t.voidable ? (
                          <button
                            type="button"
                            className="ferro-btn ferro-btn--primary"
                            onClick={() => {
                              setActionError(null);
                              setActionOk(null);
                              setRefund(null);
                              setConfirmVoidId(t.paymentId);
                            }}
                            data-testid={`void-${t.paymentId}`}
                          >
                            Void
                          </button>
                        ) : null}
                        {t.refundable ? (
                          <button
                            type="button"
                            className={
                              t.voidable
                                ? 'ferro-btn ferro-btn--ghost'
                                : 'ferro-btn ferro-btn--primary'
                            }
                            onClick={() => openRefund(t)}
                            data-testid={`refund-${t.paymentId}`}
                          >
                            {t.voidable ? 'Refund instead' : 'Refund'}
                          </button>
                        ) : null}
                        {!t.voidable && !t.refundable ? (
                          <span className="ferro-muted">—</span>
                        ) : null}
                      </>
                    )}
                  </PermissionGate>
                </div>

                {isRefundDialogOpen && refund ? (
                  <form
                    className="ferro-refund-form"
                    data-testid={`refund-form-${t.paymentId}`}
                    noValidate
                    onSubmit={(e) => {
                      e.preventDefault();
                      void doRefund();
                    }}
                  >
                    <p className="ferro-muted">
                      Refund {refund.customerName} — up to{' '}
                      {formatMoney(refund.remainingCents)} available.
                    </p>
                    <label className="ferro-field">
                      <span>Amount (blank = full {formatMoney(refund.remainingCents)})</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        placeholder={(refund.remainingCents / 100).toFixed(2)}
                        value={refund.amountText}
                        onChange={(e) =>
                          setRefund({ ...refund, amountText: e.target.value })
                        }
                        data-testid={`refund-amount-${t.paymentId}`}
                      />
                    </label>
                    <label className="ferro-field">
                      <span>Reason (optional)</span>
                      <input
                        type="text"
                        maxLength={500}
                        value={refund.reason}
                        onChange={(e) =>
                          setRefund({ ...refund, reason: e.target.value })
                        }
                        data-testid={`refund-reason-${t.paymentId}`}
                      />
                    </label>
                    <div className="ferro-table__actions">
                      <button
                        type="submit"
                        className="ferro-btn ferro-btn--primary"
                        disabled={busyId === t.paymentId}
                        data-testid={`refund-submit-${t.paymentId}`}
                      >
                        {busyId === t.paymentId
                          ? 'Refunding…'
                          : refund.amountText.trim()
                            ? `Refund $${refund.amountText.trim()}`
                            : `Refund ${formatMoney(refund.remainingCents)}`}
                      </button>
                      <button
                        type="button"
                        className="ferro-btn ferro-btn--ghost"
                        onClick={() => setRefund(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </article>
  );
}
